/**
 * Every tap-to-reveal popover in a block renders identically, so the class list lives here once.
 *
 * Three call sites now share it: the description and title helpers below, which build HTML strings,
 * and MetricCardBlock's KPI label, which is JSX and so cannot use those helpers. Duplicating the
 * string a third time is exactly how the info-icon SVG in this file ended up with two copies that
 * have to be hand-synchronised — see the warning on that glyph.
 *
 * `absolute` needs a positioned ancestor: every caller marks its title row `max-sm:relative`.
 * `bottom-full` opens the popover UPWARD, over the card above rather than over the block's own
 * content. `z-20` rather than `z-10` is load-bearing — see the note on renderBlockDescriptionHtml.
 * Sizes are exact px because the iframe's 14px root would render `text-sm` at 12.25px.
 */
export const BLOCK_POPOVER_CLASS =
  "block-popover absolute z-20 bottom-full left-0 right-0 mb-1 rounded-[6px] p-[6px] " +
  "bg-main-background text-main-foreground text-[14px] leading-[18px] font-semibold";

/**
 * Shared markup for a chart/table block's optional description, used by ChartBlock and
 * DataTableBlock (both inject this into innerHTML via useBlockRender, not JSX).
 *
 * - Desktop (>=640px, Tailwind `sm`): unchanged always-visible paragraph under the title.
 * - Mobile (<640px): the paragraph is replaced by a small tap-to-reveal info icon, to save
 *   vertical space on narrow phones (matches the Figma mobile mock).
 *
 * On mobile the icon sits INLINE TO THE RIGHT OF THE TITLE (Figma 5753:13777 "Title": flex row,
 * 8px gap, centre-aligned, 12px bottom padding, 16px icon). The caller supplies that row — see
 * the `block-title-row` wrapper in ChartBlock/DataTableBlock — so this markup deliberately owns
 * no stacking margins of its own; it just has to behave as a flex item.
 *
 * The tooltip anchors to that ROW (`.block-title-row` is `max-sm:relative`), not to the icon, and
 * stretches `left-0 right-0`. Anchoring it to the icon would shoot it off the right edge now that
 * the icon sits at the end of the title — measured 240px wide starting at x=311 on a 390px
 * viewport, then clipped by the mobile `#iframe-root { overflow-x: clip }` guard. Row-anchored, it
 * always fits whatever the title length. The tooltip only ever paints below 640px (its wrapper is
 * `hidden max-sm:inline-flex`), so it needs no desktop variants.
 *
 * Both variants are wrapped in one `.block-description` element so useBlockRender's existing
 * no-data hide logic (`querySelector(".block-description")`, toggles `display: none`) continues
 * to hide the description — in whichever variant is currently visible — without changes there.
 *
 * The disclosure is a native `<details>`/`<summary>`, not React state: this markup is a plain HTML
 * string injected via innerHTML, so there's no React tree to attach interactivity to.
 *
 * It used to be pure CSS (Tailwind `peer` + `peer-focus`/`peer-focus-within`), which made it
 * IMPOSSIBLE TO OPEN ON iOS — WebKit does not focus a `<button>` on tap (bug 22261), so the
 * `peer-focus` selector never matched and the description was simply unreachable on the platform
 * this feature was built for (it worked on Android Chrome, so it read as fine). Verified with a
 * real touch tap: chromium left `activeElement` on the button, WebKit left it on `<body>`.
 * `<details>` was verified working on tap in the same WebKit harness, and is the correct primitive
 * anyway — natively tappable, keyboard-operable and screen-reader-announced with no JS.
 *
 * Note `<details>` hides its non-`<summary>` children itself, so the description carries no `hidden`
 * class; adding one back would keep it hidden even when open. Unlike the old focus-based version it
 * does not close on blur — dismissal is the icon being tapped again, another disclosure in the same
 * row opening (see the `name` attribute below), or a tap outside it, which
 * useCloseBlockPopoversOnOutsideTap handles since `<details>` has no native outside-dismiss.
 *
 * Styling follows Figma 5753:14025 "tooltip/right": 6px radius, 6px padding, 14px/18px semibold, no
 * border, two-layer shadow. Colours are theme tokens rather than the design's literal white/#111215
 * so the tooltip stays readable on the dark themes, and `main-background` is used in preference to
 * `card-background` because the latter is translucent in several themes and would let dashboard
 * content show through. The shadow keeps the design's geometry but is black at low alpha instead of
 * its opaque Gray/200, so it reads as depth on light themes and does not glow outward on dark ones
 * (see `.block-popover` in WebDashboardViewer's srcDoc). Sizes are exact px because the iframe's 14px
 * root would render `text-sm` at 12.25px and `p-1.5` at 5.25px.
 *
 * The info icon's SVG is inlined, kept in sync with `InfoCircleOutlineIcon` in Icons/icons.tsx —
 * that React component cannot be used here because this function returns an HTML string, so the two
 * copies of the glyph have to be updated together. Note it is the OUTLINE variant
 * (`InfoCircleOutlineIcon`), not the filled `InfoCircleIcon`.
 *
 * The path carries an explicit `fill="currentColor"` — icons rendered inside the dashboard iframe
 * must set fill/stroke themselves, since the `Svg` wrapper's `fill: currentColor`
 * styled-components CSS lives in the parent document and does not apply inside the portaled iframe.
 * The svg is 14×14 inside a 16px `<summary>` box: 16px is the icon frame from Figma 5753:13777 and
 * is what gives the tap target its size, while 14 is the glyph's own artboard.
 *
 * Both popovers are `z-20`, NOT `z-10`. On data-table blocks dashboardHelpers gives the search
 * wrapper and the actions wrapper an inline `zIndex = "10"`, and both sit in the same stacking
 * context as the popover (`.block-title-row` is `position: relative` with `z-index: auto`, which
 * does not create one). At an equal z-index the later element in the DOM wins, and the table markup
 * follows the title row — so at `z-10` the search bar painted over the popover AND swallowed taps
 * across 100% of its area, measured with elementFromPoint at six points. 20 clears it; nothing
 * inside the dashboard iframe goes above 10.
 */
export const renderBlockDescriptionHtml = (
  escapedDescription: string,
  disclosureGroup: string
): string => `<div class="block-description max-sm:shrink-0 max-sm:flex max-sm:items-center">
  <p class="max-sm:hidden text-sm opacity-70 -mt-2 mb-2">${escapedDescription}</p>
  <div class="hidden max-sm:inline-flex">
    <details name="${disclosureGroup}">
    <summary
      class="relative inline-flex items-center justify-center size-[16px] rounded-full text-main-muted-foreground hover:text-main-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-button-background cursor-pointer after:absolute after:-inset-2 after:content-['']"
      aria-label="Show description"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill="currentColor" d="M6 3.33333H7.33333V4.66667H6V3.33333ZM6 6H7.33333V10H6V6ZM6.66667 0C2.98667 0 0 2.98667 0 6.66667C0 10.3467 2.98667 13.3333 6.66667 13.3333C10.3467 13.3333 13.3333 10.3467 13.3333 6.66667C13.3333 2.98667 10.3467 0 6.66667 0ZM6.66667 12C3.72667 12 1.33333 9.60667 1.33333 6.66667C1.33333 3.72667 3.72667 1.33333 6.66667 1.33333C9.60667 1.33333 12 3.72667 12 6.66667C12 9.60667 9.60667 12 6.66667 12Z" />
      </svg>
    </summary>
    <div class="${BLOCK_POPOVER_CLASS}">
      ${escapedDescription}
    </div>
    </details>
  </div>
</div>`;

/**
 * The block's title, as the `.block-title-row` heading plus — on mobile only — a tap-to-reveal
 * popover carrying the full title. Used by ChartBlock and DataTableBlock, which inject this via
 * innerHTML rather than JSX (see useBlockRender).
 *
 * Why a popover at all: mobile truncates the title to one line (`truncate`), so a long title is
 * unreadable past the ellipsis with no way to recover it. This gives it back, reusing the same
 * disclosure primitive and `.block-popover` styling as the description so the two read as one
 * pattern rather than two.
 *
 * TWO COPIES OF THE TITLE, one per breakpoint, deliberately:
 *
 * - Desktop keeps a bare `<h2>` (`max-sm:hidden`), byte-identical in layout and semantics to before
 *   this popover existed. Wrapping the single h2 in `<details>`/`<summary>` instead would have put a
 *   focusable, clickable summary in the desktop DOM — a new tab stop and a clickable heading at a
 *   width where nothing is truncated and there is nothing to reveal — needing `pointer-events-none`
 *   and marker resets to neutralise. Desktop pays nothing here instead.
 * - Mobile gets the `<details>` copy (`hidden max-sm:block`), whose `<summary>` IS the truncated
 *   heading, so the whole title is the tap target — no separate affordance to discover.
 *
 * `display: none` keeps whichever copy is inactive out of the accessibility tree, so screen readers
 * announce exactly one heading at every width. This mirrors the desktop-paragraph / mobile-icon split
 * that renderBlockDescriptionHtml already uses.
 *
 * The popover cannot be conditioned on the title ACTUALLY being truncated: CSS cannot detect
 * overflow, and this markup is a static string with no JS to measure it. So a short title is
 * tappable too and reveals the same text it already shows — harmless, and the alternative (measuring
 * every title on resize) buys little.
 *
 * `name="${disclosureGroup}"` makes this and the description mutually exclusive — opening one closes
 * the other natively, no JS. Both popovers are `absolute bottom-full left-0 right-0` against the same
 * row, so without it they would stack exactly on top of each other. The name is per-block
 * (`fullBlockId`), so opening a popover in one block leaves other blocks alone. Verified supported in
 * WebKit 26 and Chromium 26; on Safari below 17.2 `name` is ignored and both can be open at once,
 * which degrades to overlap rather than breaking either one.
 *
 * `min-w-0` on the `<details>` is what makes the ellipsis work: it is the flex item in the row, and
 * without it it would refuse to shrink below its text width. It also replaces the old `min-w-0` on
 * the h2 — `themed-card-title` is `flex-shrink-0`, but the h2 is no longer a flex child (it is a
 * block inside `<summary>`), so that no longer fights the truncation.
 *
 * `extraH2Classes` carries per-caller desktop-only concerns — DataTableBlock's right margin
 * reserving room for the search box — which belong on the desktop copy only.
 */
export const renderBlockTitleHtml = (
  escapedTitle: string,
  disclosureGroup: string,
  extraH2Classes = ""
): string => `<h2 class="themed-card-title max-sm:hidden${
  extraH2Classes ? ` ${extraH2Classes}` : ""
}">${escapedTitle}</h2>
  <details class="hidden max-sm:block min-w-0" name="${disclosureGroup}">
  <summary class="block min-w-0">
    <h2 class="themed-card-title !mb-0 truncate">${escapedTitle}</h2>
  </summary>
  <div class="${BLOCK_POPOVER_CLASS}">
    ${escapedTitle}
  </div>
  </details>`;
